import React, { useRef, useState } from 'react';
import { Download, Upload, Trash2, Moon, Sun, Save, AlertTriangle, CheckCircle, RefreshCcw } from 'lucide-react';
import { Task, Note } from '../types';

interface SettingsProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  tasks: Task[];
  notes: Note[];
  deadline: string | null;
  setTasks: (tasks: Task[]) => void;
  setNotes: (notes: Note[]) => void;
  setDeadline: (date: string | null) => void;
}

const Settings: React.FC<SettingsProps> = ({
  darkMode,
  setDarkMode,
  tasks,
  notes,
  deadline,
  setTasks,
  setNotes,
  setDeadline
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Export Data
  const handleExport = () => {
    const data = {
      tasks,
      notes,
      deadline,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `at-a-glance-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import Data
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        
        // Basic validation
        if (Array.isArray(json.tasks)) setTasks(json.tasks);
        if (Array.isArray(json.notes)) setNotes(json.notes);
        if (json.deadline || json.deadline === null) setDeadline(json.deadline);
        
        setImportStatus('success');
        setTimeout(() => setImportStatus('idle'), 3000);
      } catch (err) {
        console.error('Import failed', err);
        setImportStatus('error');
        setTimeout(() => setImportStatus('idle'), 3000);
      }
    };
    reader.readAsText(file);
    // Reset input
    e.target.value = ''; 
  };

  const handleClearData = () => {
    if (window.confirm('آیا مطمئن هستید؟ تمام اطلاعات شما پاک خواهد شد و قابل بازگشت نیست.')) {
      setTasks([]);
      setNotes([]);
      setDeadline(null);
      localStorage.clear();
      window.location.reload();
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-20">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-900 dark:to-slate-800 rounded-3xl p-8 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2">تنظیمات</h2>
        <p className="text-slate-300">مدیریت داده‌ها و شخصی‌سازی ظاهر برنامه</p>
      </div>

      {/* Appearance Section */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800">
        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
          <Sun size={20} className="text-orange-500" />
          ظاهر برنامه
        </h3>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-200">حالت تاریک (Dark Mode)</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">تغییر رنگ‌های برنامه برای مطالعه در شب</p>
          </div>
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className={`
              w-14 h-8 rounded-full p-1 transition-colors duration-300 relative
              ${darkMode ? 'bg-indigo-600' : 'bg-slate-300'}
            `}
          >
            <div className={`
              w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center
              ${darkMode ? '-translate-x-6' : 'translate-x-0'}
            `}>
              {darkMode ? <Moon size={14} className="text-indigo-600" /> : <Sun size={14} className="text-orange-500" />}
            </div>
          </button>
        </div>
      </div>

      {/* Data Management Section */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-800">
        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
          <Save size={20} className="text-blue-500" />
          مدیریت داده‌ها
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export */}
          <button 
            onClick={handleExport}
            className="flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-slate-100 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
          >
            <div className="bg-blue-100 dark:bg-blue-900/50 p-2 rounded-lg text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
              <Download size={24} />
            </div>
            <div className="text-right">
              <p className="font-bold text-slate-700 dark:text-slate-200">پشتیبان‌گیری</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">دانلود فایل JSON</p>
            </div>
          </button>

          {/* Import */}
          <button 
            onClick={handleImportClick}
            className={`
              flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all group relative overflow-hidden
              ${importStatus === 'error' ? 'border-red-200 bg-red-50' : 
                importStatus === 'success' ? 'border-green-200 bg-green-50' : 
                'border-slate-100 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'}
            `}
          >
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept=".json" 
              onChange={handleFileChange}
            />
            
            {importStatus === 'idle' && (
              <>
                <div className="bg-indigo-100 dark:bg-indigo-900/50 p-2 rounded-lg text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                  <Upload size={24} />
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-700 dark:text-slate-200">بازگردانی اطلاعات</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">آپلود فایل JSON</p>
                </div>
              </>
            )}

            {importStatus === 'success' && (
              <div className="flex flex-col items-center text-green-600">
                <CheckCircle size={28} className="mb-1" />
                <span className="text-sm font-bold">با موفقیت انجام شد</span>
              </div>
            )}

            {importStatus === 'error' && (
              <div className="flex flex-col items-center text-red-600">
                <AlertTriangle size={28} className="mb-1" />
                <span className="text-sm font-bold">خطا در فایل</span>
              </div>
            )}
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-red-500">
              <AlertTriangle size={20} />
              <div>
                <p className="font-bold text-sm">منطقه خطر</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">پاک کردن تمام اطلاعات ذخیره شده در مرورگر</p>
              </div>
            </div>
            <button 
              onClick={handleClearData}
              className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors flex items-center gap-2"
            >
              <Trash2 size={16} />
              حذف همه
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;